import React, { useState, useEffect } from 'react';

// Use absolute URL instead of relative to avoid proxy issues
const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';

const MasterTypeList = ({ conferenceId }) => {
  const [masterTypes, setMasterTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [typeDetails, setTypeDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateMasterTypeModal, setShowCreateMasterTypeModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditMasterTypeModal, setShowEditMasterTypeModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingMasterType, setEditingMasterType] = useState(null);
  const [newItemValue, setNewItemValue] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemSessionDate, setNewItemSessionDate] = useState('');
  const [newMasterTypeName, setNewMasterTypeName] = useState('');
  const [newMasterTypeDescription, setNewMasterTypeDescription] = useState('');
  const [newMasterTypeIsActive, setNewMasterTypeIsActive] = useState(true);

  const fetchMasterTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${CONFERENCE_API}/${conferenceId}/master/type`;
      console.log('🔍 Fetching master types for conference:', conferenceId, 'URL:', url);
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        console.log('📋 Master types received:', data);
        setMasterTypes(data || []);
        
        // Only auto-select if nothing is selected
        if (!selectedType && data && data.length > 0) {
          console.log('🎯 Auto-selecting first type');
          setSelectedType(data[0]);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch master types:', errorText);
        setError(`Failed to fetch master types: ${errorText}`);
        setMasterTypes([]);
      }
    } catch (err) {
      console.error('❌ Error fetching master types:', err);
      setError(`Error: ${err.message}`);
      setMasterTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTypeDetails = async (parentId) => {
    if (!parentId) {
      setTypeDetails([]);
      return;
    }
    
    setDetailsLoading(true);
    setError(null);
    try {
      const url = `${CONFERENCE_API}/master/type/${parentId}/details`;
      console.log('🔍 Fetching type details from:', url);
      
      const response = await fetch(url);
      console.log('📡 Details response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📦 Type details received:', data);
        setTypeDetails(Array.isArray(data) ? data : []);
      } else if (response.status === 404) {
        console.log('📭 No details found (404)');
        setTypeDetails([]);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch details:', errorText);
        setError(`Failed to fetch details: ${errorText}`);
        setTypeDetails([]);
      }
    } catch (err) {
      console.error('❌ Error fetching type details:', err);
      setError(`Error: ${err.message}`);
      setTypeDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Fetch master types when conference changes
  useEffect(() => {
    if (conferenceId) {
      console.log('🔄 Conference changed to:', conferenceId);
      setMasterTypes([]);
      setSelectedType(null);
      setTypeDetails([]);
      setError(null);
      fetchMasterTypes();
    }
  }, [conferenceId]);

  // Fetch details when selected type changes
  useEffect(() => {
    if (selectedType?.id) {
      console.log('🎯 Fetching details for type ID:', selectedType.id);
      fetchTypeDetails(selectedType.id);
    } else {
      setTypeDetails([]);
    }
  }, [selectedType?.id]);

  const handleCreateMasterType = async (e) => {
    e.preventDefault();
    if (!newMasterTypeName.trim()) {
      alert('Please enter a master type name');
      return;
    }

    try {
      const payload = {
        definition: newMasterTypeName,
        isActive: true
      };

      console.log('Creating master type with payload:', payload);
      const response = await fetch(
        `${CONFERENCE_API}/${conferenceId}/master/type`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        await fetchMasterTypes();
        setNewMasterTypeName('');
        setNewMasterTypeDescription('');
        setShowCreateMasterTypeModal(false);
        alert('Master type created successfully!');
      } else {
        const errorText = await response.text();
        alert(`Failed to create master type: ${errorText}`);
      }
    } catch (err) {
      console.error('Error creating master type:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    if (!selectedType) return;

    try {
      const payload = {
        value: newItemValue,
        description: newItemDescription
      };

      // Add session date if provided (format: dd-MM-yyyy)
      if (newItemSessionDate) {
        const [year, month, day] = newItemSessionDate.split('-');
        payload.sessionDate = `${day}-${month}-${year}`;
      }

      console.log('Creating detail for master type ID:', selectedType.id, 'with payload:', payload);
      const response = await fetch(
        `${CONFERENCE_API}/${conferenceId}/master/type/${selectedType.id}/details`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        // Refresh the details list
        await fetchTypeDetails(selectedType.id);
        // Reset form
        setNewItemValue('');
        setNewItemDescription('');
        setNewItemSessionDate('');
        setShowCreateModal(false);
        alert('Item created successfully!');
      } else {
        const errorText = await response.text();
        alert(`Failed to create item: ${errorText}`);
      }
    } catch (err) {
      console.error('Error creating item:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleEditMasterType = (masterType) => {
    setEditingMasterType(masterType);
    setNewMasterTypeName(masterType.definition);
    setNewMasterTypeIsActive(masterType.isActive !== false);
    setShowEditMasterTypeModal(true);
  };

  const handleUpdateMasterType = async (e) => {
    e.preventDefault();
    if (!editingMasterType || !newMasterTypeName.trim()) return;

    try {
      const payload = {
        id: editingMasterType.id,
        definition: newMasterTypeName,
        isActive: newMasterTypeIsActive,
        conferenceId: editingMasterType.conferenceId
      };

      console.log('Updating master type:', payload);
      const response = await fetch(
        `${CONFERENCE_API}/${conferenceId}/master/type`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        await fetchMasterTypes();
        setShowEditMasterTypeModal(false);
        setEditingMasterType(null);
        setNewMasterTypeName('');
        setNewMasterTypeIsActive(true);
        alert('Master type updated successfully!');
      } else {
        const errorText = await response.text();
        alert(`Failed to update master type: ${errorText}`);
      }
    } catch (err) {
      console.error('Error updating master type:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleDeleteMasterType = async (masterType) => {
    if (!window.confirm(`Are you sure you want to delete "${masterType.definition}"?\n\nThis will also delete all associated details and conference fees. This action cannot be undone.`)) {
      return;
    }

    try {
      console.log('Deleting master type:', masterType.id);
      const response = await fetch(
        `${CONFERENCE_API}/${conferenceId}/master/type/${masterType.id}`,
        {
          method: 'DELETE'
        }
      );

      if (response.ok) {
        // If deleted type was selected, clear selection
        if (selectedType?.id === masterType.id) {
          setSelectedType(null);
          setTypeDetails([]);
        }
        await fetchMasterTypes();
        alert('Master type deleted successfully!');
      } else {
        const errorText = await response.text();
        alert(`Failed to delete master type: ${errorText}`);
      }
    } catch (err) {
      console.error('Error deleting master type:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setNewItemValue(item.value);
    setNewItemDescription(item.description || '');
    // Convert date from dd-MM-yyyy to yyyy-MM-dd for input field
    if (item.sessionDate) {
      const [day, month, year] = item.sessionDate.split('-');
      setNewItemSessionDate(`${year}-${month}-${day}`);
    } else {
      setNewItemSessionDate('');
    }
    setShowEditModal(true);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!editingItem) return;

    try {
      const payload = {
        id: editingItem.id,
        value: newItemValue,
        description: newItemDescription
      };

      // Add session date if provided (format: dd-MM-yyyy)
      if (newItemSessionDate) {
        const [year, month, day] = newItemSessionDate.split('-');
        payload.sessionDate = `${day}-${month}-${year}`;
      }

      console.log('Updating detail item:', payload);
      const response = await fetch(
        `${CONFERENCE_API}/${conferenceId}/master/type/${selectedType.id}/details`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        await fetchTypeDetails(selectedType.id);
        setShowEditModal(false);
        setEditingItem(null);
        setNewItemValue('');
        setNewItemDescription('');
        setNewItemSessionDate('');
        alert('Item updated successfully!');
      } else {
        const errorText = await response.text();
        alert(`Failed to update item: ${errorText}`);
      }
    } catch (err) {
      console.error('Error updating item:', err);
      alert(`Error: ${err.message}`);
    }
  };

  // Delete functionality removed - use edit to mark items as inactive instead

  if (!conferenceId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
        <p>Please select a conference first</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', gap: '20px', height: 'calc(100vh - 200px)' }}>
        {/* Left Panel - Master Type List */}
        <div style={{ 
          width: '300px', 
          borderRight: '1px solid #dee2e6',
          paddingRight: '20px',
          overflowY: 'auto'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '15px',
            gap: '10px'
          }}>
            <h3 style={{ margin: 0 }}>Master Types</h3>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button
                onClick={() => setShowCreateMasterTypeModal(true)}
                className="btn btn-primary"
                style={{
                  padding: '5px 10px',
                  fontSize: '12px'
                }}
              >
                ➕ Add Master Type
              </button>
              <button
                onClick={fetchMasterTypes}
                disabled={loading}
                style={{
                  padding: '5px 10px',
                  fontSize: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                🔄
              </button>
            </div>
          </div>

          {loading && <div>Loading master types...</div>}
          
          {error && (
            <div style={{ 
              padding: '10px', 
              backgroundColor: '#fee', 
              color: '#c00',
              borderRadius: '4px',
              marginBottom: '10px',
              fontSize: '13px'
            }}>
              {error}
            </div>
          )}

          {!loading && masterTypes.length === 0 && (
            <div style={{ color: '#7f8c8d', fontSize: '14px' }}>
              No master types found for this conference
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {masterTypes.map((type) => (
              <div
                key={type.id}
                style={{
                  padding: '12px',
                  borderRadius: '6px',
                  border: selectedType?.id === type.id ? '2px solid #1976d2' : '1px solid #dee2e6',
                  backgroundColor: selectedType?.id === type.id ? '#e3f2fd' : 'white',
                  transition: 'all 0.2s'
                }}
              >
                <div 
                  onClick={() => {
                    // Only update if selecting a different type
                    if (selectedType?.id !== type.id) {
                      console.log('👆 User clicked type:', type.id);
                      setSelectedType(type);
                    } else {
                      console.log('👆 Already selected:', type.id);
                    }
                  }}
                  style={{ cursor: 'pointer', flex: 1 }}
                >
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>
                    {type.definition || 'Unnamed Type'}
                  </div>
                  {type.details && type.details.length > 0 && (
                    <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '4px' }}>
                      {type.details.length} item{type.details.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditMasterType(type);
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      border: '1px solid #1976d2',
                      borderRadius: '3px',
                      background: 'white',
                      color: '#1976d2',
                      cursor: 'pointer'
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteMasterType(type);
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      border: '1px solid #dc3545',
                      borderRadius: '3px',
                      background: 'white',
                      color: '#dc3545',
                      cursor: 'pointer'
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Type Details */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {!selectedType ? (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: '#7f8c8d' 
            }}>
              <p>Select a master type from the left to view details</p>
            </div>
          ) : (
            <>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '20px',
                paddingBottom: '15px',
                borderBottom: '2px solid #ecf0f1'
              }}>
                <div>
                  <h2 style={{ margin: 0, color: '#2c3e50' }}>{selectedType.name}</h2>
                  <p style={{ margin: '5px 0 0 0', color: '#7f8c8d', fontSize: '14px' }}>
                    {selectedType.description || 'No description'}
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn btn-primary"
                  style={{ padding: '10px 20px' }}
                >
                  ➕ Add Detail Type
                </button>
              </div>

              {detailsLoading && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#7f8c8d' }}>
                  Loading details...
                </div>
              )}

              {!detailsLoading && typeDetails.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  color: '#7f8c8d'
                }}>
                  <p>No items found for this master type</p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn btn-primary"
                    style={{ marginTop: '15px' }}
                  >
                    Create First Item
                  </button>
                </div>
              )}

              {!detailsLoading && typeDetails.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {typeDetails.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        padding: '15px',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontWeight: '600', 
                          fontSize: '15px', 
                          color: '#2c3e50',
                          marginBottom: '5px'
                        }}>
                          {item.value}
                        </div>
                        {item.description && (
                          <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                            {item.description}
                          </div>
                        )}
                        {item.sessionDate && (
                          <div style={{ fontSize: '12px', color: '#1976d2', marginTop: '3px' }}>
                            📅 {item.sessionDate}
                          </div>
                        )}
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#95a5a6', 
                          marginTop: '5px' 
                        }}>
                          ID: {item.id} | Active: {item.isActive ? '✓' : '✗'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleEditItem(item)}
                          className="btn btn-secondary"
                          style={{ 
                            padding: '6px 12px',
                            fontSize: '13px'
                          }}
                        >
                          ✏️ Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Master Type Modal */}
      {showCreateMasterTypeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
              Add New Master Type
            </h3>
            <form onSubmit={handleCreateMasterType}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Master Type Name *
                </label>
                <input
                  type="text"
                  value={newMasterTypeName}
                  onChange={(e) => setNewMasterTypeName(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., Category, Department, Gender"
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Description (optional)
                </label>
                <textarea
                  value={newMasterTypeDescription}
                  onChange={(e) => setNewMasterTypeDescription(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  placeholder="Enter description"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateMasterTypeModal(false);
                    setNewMasterTypeName('');
                    setNewMasterTypeDescription('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Create Master Type
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Detail Type Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
              Add New {selectedType?.name} Detail
            </h3>
            <form onSubmit={handleCreateItem}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Value/Name *
                </label>
                <input
                  type="text"
                  value={newItemValue}
                  onChange={(e) => setNewItemValue(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter value"
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Description (optional)
                </label>
                <textarea
                  value={newItemDescription}
                  onChange={(e) => setNewItemDescription(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  placeholder="Enter description"
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Session Date (optional)
                </label>
                <input
                  type="date"
                  value={newItemSessionDate}
                  onChange={(e) => setNewItemSessionDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewItemValue('');
                    setNewItemDescription('');
                    setNewItemSessionDate('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Master Type Modal */}
      {showEditMasterTypeModal && editingMasterType && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
              Edit Master Type
            </h3>
            <form onSubmit={handleUpdateMasterType}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Master Type Name *
                </label>
                <input
                  type="text"
                  value={newMasterTypeName}
                  onChange={(e) => setNewMasterTypeName(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newMasterTypeIsActive}
                    onChange={(e) => setNewMasterTypeIsActive(e.target.checked)}
                    style={{
                      marginRight: '8px',
                      cursor: 'pointer',
                      width: '18px',
                      height: '18px'
                    }}
                  />
                  <span style={{ fontWeight: '500' }}>Active</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditMasterTypeModal(false);
                    setEditingMasterType(null);
                    setNewMasterTypeName('');
                    setNewMasterTypeIsActive(true);
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Detail Type Modal */}
      {showEditModal && editingItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
              Edit {selectedType?.name} Detail
            </h3>
            <form onSubmit={handleUpdateItem}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Value/Name *
                </label>
                <input
                  type="text"
                  value={newItemValue}
                  onChange={(e) => setNewItemValue(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Description (optional)
                </label>
                <textarea
                  value={newItemDescription}
                  onChange={(e) => setNewItemDescription(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Session Date (optional)
                </label>
                <input
                  type="date"
                  value={newItemSessionDate}
                  onChange={(e) => setNewItemSessionDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                    setNewItemValue('');
                    setNewItemDescription('');
                    setNewItemSessionDate('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterTypeList;
